import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, Calendar, TrendingUp, Clock } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/analytics/dashboard');
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      title: 'Recovered Revenue',
      value: analytics ? `$${analytics.recovered_revenue.toFixed(2)}` : '$0.00',
      icon: DollarSign,
      color: 'bg-teal-100 text-teal-700',
      testId: 'recovered-revenue-card',
    },
    {
      title: 'Recovered Slots',
      value: analytics?.recovered_slots || 0,
      icon: Calendar,
      color: 'bg-blue-100 text-blue-700',
      testId: 'recovered-slots-card',
    },
    {
      title: 'Fill Success Rate',
      value: analytics ? `${analytics.fill_success_rate.toFixed(1)}%` : '0%',
      icon: TrendingUp,
      color: 'bg-green-100 text-green-700',
      testId: 'success-rate-card',
    },
    {
      title: 'Avg. Time to Refill',
      value: analytics ? `${analytics.avg_time_to_refill.toFixed(0)} min` : '0 min',
      icon: Clock,
      color: 'bg-amber-100 text-amber-700',
      testId: 'avg-time-card',
    },
  ];

  return (
    <Layout active="dashboard">
      <div data-testid="dashboard-page" className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
            Dashboard
          </h1>
          <p className="text-gray-600 mt-2">Track your revenue recovery performance</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card
                    key={stat.title}
                    data-testid={stat.testId}
                    className="bg-white border-border shadow-sm hover:border-teal-500/50 transition-all"
                  >
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600">
                        {stat.title}
                      </CardTitle>
                      <div className={`p-2 rounded-md ${stat.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
                        {stat.value}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white border-border shadow-sm">
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics?.total_cancellations === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        No cancellations detected yet. Connect your Google Calendar to start monitoring.
                      </p>
                    ) : (
                      <p className="text-gray-600">
                        {analytics?.total_cancellations} cancellations detected
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-border shadow-sm">
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <a
                      href="/staff"
                      className="block p-3 rounded-md border border-border hover:border-teal-500 transition-all"
                    >
                      <p className="font-medium">Add Staff Members</p>
                      <p className="text-sm text-gray-600">Connect their Google Calendars</p>
                    </a>
                    <a
                      href="/services"
                      className="block p-3 rounded-md border border-border hover:border-teal-500 transition-all"
                    >
                      <p className="font-medium">Define Services</p>
                      <p className="text-sm text-gray-600">Set prices for revenue tracking</p>
                    </a>
                    <a
                      href="/waitlist"
                      className="block p-3 rounded-md border border-border hover:border-teal-500 transition-all"
                    >
                      <p className="font-medium">Build Waitlist</p>
                      <p className="text-sm text-gray-600">Add customers who want slots</p>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
